import type { Product, ProductVariant } from "@/types";

type DbProductLike = {
  id: string;
  title: string;
  slug: string;
  brand: string;
  category: string;
  categories?: unknown;
  description: string;
  specifications?: unknown;
  variants?: unknown;
  images: unknown;
  price: number;
  mrp: number;
  discount: number;
  stock: number;
  rating: number;
  reviewCount: number;
  tags: unknown;
  isPrime: boolean;
  isFeatured: boolean;
  isSponsored?: boolean;
  isFlashDeal?: boolean;
  inPromotionBanner?: boolean;
  flashEndsAt?: Date | string | null;
  seller?: string | null;
  warranty?: string | null;
  vendorId?: string | null;
  userId?: string | null;
  sellerPhone?: string | null;
  rejectionReason?: string | null;
  createdAt?: Date | string | null;
  status?: unknown;
};

function parseStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item).trim()).filter(Boolean);
      }
    } catch {
      if (value.trim()) return [value.trim()];
    }
  }
  return [];
}

/** Older rows only carry the single `category` column, so fall back to it. */
function normalizeCategories(value: unknown, fallback: string): string[] {
  const parsed = parseStringArray(value);
  return [...new Set([fallback.trim(), ...parsed].filter(Boolean))];
}

function parseVariants(value: unknown): ProductVariant[] {
  let list: any[] = [];
  if (Array.isArray(value)) {
    list = value;
  } else if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) list = parsed;
    } catch {
      list = [];
    }
  }
  return list
    .map((v) => ({
      id: String(v.id || ""),
      sku: v.sku ? String(v.sku) : undefined,
      title: v.title ? String(v.title) : undefined,
      color: v.color ? String(v.color) : undefined,
      colorCode: v.colorCode ? String(v.colorCode) : undefined,
      image: String(v.image || ""),
      images: Array.isArray(v.images) ? v.images.map(String) : undefined,
      price: Number(v.price || 0),
      mrp: v.mrp !== undefined ? Number(v.mrp) : undefined,
      stock: Number(v.stock ?? 0),
      attributes: v.attributes && typeof v.attributes === "object" ? v.attributes : undefined,
      isActive: v.isActive !== false,
    }))
    .filter((v) => v.id && v.price > 0);
}

export function serializeProduct(product: DbProductLike): Product {
  return {
    id: product.id,
    title: product.title,
    slug: product.slug,
    brand: product.brand,
    category: product.category,
    categories: normalizeCategories(product.categories, product.category),
    description: product.description,
    specifications: (product.specifications as Record<string, string> | null | undefined) ?? undefined,
    variants: parseVariants(product.variants),
    images: parseStringArray(product.images),
    price: product.price,
    mrp: product.mrp,
    discount: product.discount,
    stock: product.stock,
    rating: product.rating,
    reviewCount: product.reviewCount,
    tags: parseStringArray(product.tags),
    isPrime: product.isPrime,
    isFeatured: product.isFeatured,
    isSponsored: product.isSponsored ?? false,
    isFlashDeal: product.isFlashDeal ?? false,
    inPromotionBanner: product.inPromotionBanner ?? false,
    flashEndsAt: product.flashEndsAt ? new Date(product.flashEndsAt).toISOString() : undefined,
    seller: product.seller ?? undefined,
    warranty: product.warranty ?? undefined,
    vendorId: product.vendorId ?? undefined,
    userId: (product as any).userId ?? undefined,
    sellerPhone: (product as any).sellerPhone ?? undefined,
    rejectionReason: (product as any).rejectionReason ?? undefined,
    createdAt: (product as any).createdAt ? new Date((product as any).createdAt).toISOString() : undefined,
    status: (product.status as string) || "APPROVED"
  };
}
